import React from 'react'
import { IconChevronDown } from '@tabler/icons-react'
import { Icon as AlertIcon } from '@enamelai/design-system/icons/Alert'
import { Icon as ArrowCircleRightIcon } from '@enamelai/design-system/icons/Arrow_circle_right'
import { Icon as Checkmark2Icon } from '@enamelai/design-system/icons/Checkmark2'
import { Icon as Delete2Icon } from '@enamelai/design-system/icons/Delete2'
import { Icon as FilesPdfIcon } from '@enamelai/design-system/icons/Files_pdf'
import { Icon as UploadIcon } from '@enamelai/design-system/icons/Upload'
import {
  Badge,
  Button,
  InputField,
  Modal,
  MODAL_FILE_DETAILS_DEMO_ITEMS,
  MODAL_MOVE_FILES_DEMO_FOLDERS,
  Search,
  Stepper,
  type ModalSize,
} from '@enamelai/design-system'
const STATIC_MODAL_PROPS = {
  inline: true,
  open: true,
  size: 'sm' as ModalSize,
  closeOnBackdrop: false,
  showClose: false,
}

type ModalFormField = 'phone' | 'email' | 'message'

type ModalFormValues = Record<ModalFormField, string>

export function getModalFormSample(
  formValues?: ModalFormValues,
  onFormValueChange?: (field: ModalFormField, value: string) => void,
): React.ReactNode {
  return (
    <div className="modal-showcase__form">
      <InputField
        label="Phone number"
        variant="phone"
        placeholder="+1 (201) 555-0179"
        value={formValues ? formValues.phone : undefined}
        onChange={
          formValues
            ? (event) => onFormValueChange?.('phone', event.target.value)
            : undefined
        }
      />
      <InputField
        label="Email"
        variant="email"
        placeholder="address@email.com"
        value={formValues ? formValues.email : undefined}
        onChange={
          formValues
            ? (event) => onFormValueChange?.('email', event.target.value)
            : undefined
        }
      />
      <InputField
        label="Message"
        variant="message"
        placeholder="Write a message"
        rows={4}
        value={formValues ? formValues.message : undefined}
        onChange={
          formValues
            ? (event) => onFormValueChange?.('message', event.target.value)
            : undefined
        }
      />
    </div>
  )
}

const LEGAL_COPY = (
  <div className="modal-showcase__legal-copy">
    <ul>
      <li>Section 1: Acceptance of Terms</li>
    </ul>
    <p>
      Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod
      tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim
      veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea
      commodo consequat.
    </p>
    <ul>
      <li>Section 2: Privacy Policy</li>
    </ul>
    <p>
      Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod
      tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim
      veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea
      commodo consequat.
    </p>
    <ul>
      <li>Section 3: User Conduct</li>
    </ul>
    <p>
      Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod
      tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim
      veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea
      commodo consequat.
    </p>
  </div>
)

function getMoveAppointmentBody(): React.ReactNode {
  return (
    <div className="modal-showcase__move">
      <p className="modal-showcase__move-lead">
        You will be moving this appointment
      </p>
      <Search className="modal-showcase__move-search" placeholder="Search" />
      <div className="modal-showcase__move-card">
        <div className="modal-showcase__move-side">
          <span className="modal-showcase__move-label">From</span>
          <strong className="modal-showcase__move-room">Room 3</strong>
          <span className="modal-showcase__move-provider">Dr. John Smith</span>
          <span className="modal-showcase__move-meta">
            06/10/2026 - 10:00 AM
          </span>
        </div>
        <span className="modal-showcase__move-arrow" aria-hidden="true">
          <span className="modal-showcase__move-arrow-inner">
            <ArrowCircleRightIcon
              className="modal-showcase__move-arrow-icon"
              width={16}
              height={16}
            />
          </span>
        </span>
        <div className="modal-showcase__move-side modal-showcase__move-side--to">
          <span className="modal-showcase__move-label">To</span>
          <strong className="modal-showcase__move-room">Room 4</strong>
          <span className="modal-showcase__move-provider">Dr. John Smith</span>
          <span className="modal-showcase__move-meta">
            06/10/2026 - 10:00 AM
          </span>
        </div>
      </div>
    </div>
  )
}

function getNewConversationBody(): React.ReactNode {
  return (
    <div className="modal-showcase__conversation">
      <Search placeholder="Search" />
      <div className="modal-showcase__conversation-contacts">
        <span className="modal-showcase__conversation-label">
          Suggested contacts
        </span>
        <div className="modal-showcase__conversation-badges">
          {['Sarah J.', 'Marcus K.', 'Anita A.', 'Brian L.', 'Chloe M.'].map(
            (name) => (
              <Badge key={name} label={name} mode="tinted" color="gray" />
            ),
          )}
        </div>
      </div>
      <InputField
        label="Message"
        variant="message"
        placeholder="Write a message"
        rows={4}
        showCounter
        maxLength={100}
      />
    </div>
  )
}

const NOTICE_COPY = (
  <>
    <p>We use cookies to improve your experience.</p>
    <p>
      By continuing, you agree to our{' '}
      <button type="button" className="ds-modal__notice-link">
        cookie policy
      </button>{' '}
      and{' '}
      <button type="button" className="ds-modal__notice-link">
        terms and conditions
      </button>
      .
    </p>
  </>
)

/** Modal / Import file */
function getImportFileBody(): React.ReactNode {
  return (
    <div className="modal-showcase__dropzone modal-showcase__dropzone--import">
      <UploadIcon
        className="modal-showcase__dropzone-icon modal-showcase__dropzone-icon--photo"
        width={80}
        height={80}
        aria-hidden="true"
      />
      <div className="modal-showcase__dropzone-copy">
        <p className="modal-showcase__dropzone-primary modal-showcase__dropzone-primary--import">
          Drag and drop file here
        </p>
        <p className="modal-showcase__dropzone-secondary modal-showcase__dropzone-secondary--import">
          <span>or choose file to upload</span>
          <span>(X MB Max size)</span>
        </p>
      </div>
      <Button variant="secondary" size="sm">
        Choose file
      </Button>
    </div>
  )
}

/** Modal / Edit photo */
function getEditPhotoBody(): React.ReactNode {
  return (
    <div className="modal-showcase__dropzone modal-showcase__dropzone--photo">
      <UploadIcon
        className="modal-showcase__dropzone-icon modal-showcase__dropzone-icon--photo"
        width={80}
        height={80}
        aria-hidden="true"
      />
      <div className="modal-showcase__dropzone-copy">
        <p className="modal-showcase__dropzone-primary modal-showcase__dropzone-primary--import">
          Drag and drop file here
        </p>
        <p className="modal-showcase__dropzone-secondary modal-showcase__dropzone-secondary--import">
          <span>or choose file to upload</span>
          <span>(X MB max size)</span>
        </p>
      </div>
      <div className="modal-showcase__dropzone-actions">
        <Button variant="secondary" size="sm">
          Take photo
        </Button>
        <Button variant="primary" size="sm">
          Upload file
        </Button>
      </div>
    </div>
  )
}

export type ModalRecipe = {
  id: string
  label: string
  node: React.ReactNode
}

export function getModalRecipes(): ModalRecipe[] {
  return [
    {
      id: 'add-info',
      label: 'Add info',
      node: (
        <Modal
          {...STATIC_MODAL_PROPS}
          title="Add info"
          primaryLabel="Primary"
          secondaryLabel="Secondary"
        >
          {getModalFormSample()}
        </Modal>
      ),
    },
    {
      id: 'file-details',
      label: 'File details',
      node: (
        <Modal
          {...STATIC_MODAL_PROPS}
          showClose
          variant="fileDetails"
          title="File details"
          fileIcon={<FilesPdfIcon width={48} height={48} aria-hidden="true" />}
          fileName="Files and letters backend documentation.pdf"
          fileMeta="PDF Document 0.39 MB"
          details={MODAL_FILE_DETAILS_DEMO_ITEMS}
          primaryLabel=""
          secondaryLabel="Close"
        />
      ),
    },
    {
      id: 'move-files',
      label: 'Move files',
      node: (
        <Modal
          {...STATIC_MODAL_PROPS}
          size="md"
          showClose
          variant="moveFiles"
          title="Move files"
          folders={MODAL_MOVE_FILES_DEMO_FOLDERS}
          defaultSelectedFolderId="claims"
          primaryLabel="Move"
          secondaryLabel="Cancel"
          footerStart={
            <Button variant="tertiary" size="sm">
              New folder
            </Button>
          }
        />
      ),
    },
    {
      id: 'unsaved-changes',
      label: 'Unsaved changes',
      node: (
        <Modal
          {...STATIC_MODAL_PROPS}
          showClose
          variant="confirmation"
          title="Unsaved changes"
          description="You have unsaved changes that will be lost if you leave this page."
          icon={<AlertIcon width={16} height={16} aria-hidden="true" />}
          iconTone="warning"
          primaryLabel="Leave page"
          secondaryLabel="Stay"
        />
      ),
    },
    {
      id: 'delete-confirmation',
      label: 'Delete appointment? (confirmation)',
      node: (
        <Modal
          {...STATIC_MODAL_PROPS}
          showClose
          variant="confirmation"
          title="Delete appointment?"
          description="Modal body description text here."
          icon={<Delete2Icon width={16} height={16} aria-hidden="true" />}
          iconTone="danger"
          primaryLabel="Delete"
          secondaryLabel="Cancel"
          primaryDestructive
        />
      ),
    },
    {
      id: 'payment-successful',
      label: 'Payment successful',
      node: (
        <Modal
          {...STATIC_MODAL_PROPS}
          showClose
          variant="confirmation"
          title="Payment successful"
          description="Modal body description text here."
          icon={<Checkmark2Icon width={16} height={16} aria-hidden="true" />}
          iconTone="success"
          primaryLabel="View receipt"
          secondaryLabel="Close"
        />
      ),
    },
    {
      id: 'title-description',
      label: 'Title description',
      node: (
        <Modal
          {...STATIC_MODAL_PROPS}
          size="md"
          showClose
          className="modal-showcase__title-description"
          title="Title description"
          primaryLabel="Delete"
          secondaryLabel="Cancel"
          bodyClassName="modal-showcase__body--title-description"
        >
          {LEGAL_COPY}
        </Modal>
      ),
    },
    {
      id: 'delete-default',
      label: 'Delete appointment? (default)',
      node: (
        <Modal
          {...STATIC_MODAL_PROPS}
          title="Delete appointment?"
          description="Modal body description text here."
          primaryLabel="Delete"
          secondaryLabel="Cancel"
          primaryDestructive
        />
      ),
    },
    {
      id: 'setup-wizard',
      label: 'Setup wizard',
      node: (
        <Modal
          {...STATIC_MODAL_PROPS}
          title="Setup wizard"
          primaryLabel="Leave page"
          secondaryLabel="Stay"
        >
          <div className="modal-showcase__wizard">
            <Stepper
              steps={['Step 1', 'Step 2', 'Step 3', 'Step 4']}
              currentStep={0}
            />
            <p className="modal-showcase__wizard-copy">
              You have unsaved changes that will be lost if you leave this page.
            </p>
          </div>
        </Modal>
      ),
    },
    {
      id: 'new-conversation',
      label: 'New conversation',
      node: (
        <Modal
          {...STATIC_MODAL_PROPS}
          title="New conversation"
          primaryLabel="Send message"
          secondaryLabel="Cancel"
          primaryDisabled
        >
          {getNewConversationBody()}
        </Modal>
      ),
    },
    {
      id: 'notice',
      label: 'Notice',
      node: (
        <Modal
          {...STATIC_MODAL_PROPS}
          size="md"
          variant="notice"
          title="Notice"
          icon={<AlertIcon width={16} height={16} aria-hidden="true" />}
          iconTone="warning"
          primaryLabel="Sign out"
          secondaryLabel="Cancel"
          showClose={false}
        >
          {NOTICE_COPY}
        </Modal>
      ),
    },
    {
      id: 'import-file',
      label: 'Import file',
      node: (
        <Modal
          {...STATIC_MODAL_PROPS}
          title="Import file"
          primaryLabel="Upload"
          secondaryLabel="Cancel"
        >
          {getImportFileBody()}
        </Modal>
      ),
    },
    {
      id: 'edit-photo',
      label: 'Edit photo',
      node: (
        <Modal
          {...STATIC_MODAL_PROPS}
          size="md"
          showClose
          title="Edit photo"
          showFooter={false}
        >
          {getEditPhotoBody()}
        </Modal>
      ),
    },
    {
      id: 'move-appointment',
      label: 'Move appointment?',
      node: (
        <Modal
          {...STATIC_MODAL_PROPS}
          showClose
          title="Move appointment?"
          primaryLabel="Move with provider"
          secondaryLabel="Switch provider"
          secondaryRightIcon={
            <IconChevronDown size={16} stroke={2} aria-hidden="true" />
          }
          footerStart={
            <Button variant="tertiary" size="sm">
              Cancel
            </Button>
          }
        >
          {getMoveAppointmentBody()}
        </Modal>
      ),
    },
  ]
}

/** Pattern options for the Modal showcase properties panel (mirrors recipes). */
export function getModalShowcasePatterns(): Array<{
  id: string
  label: string
}> {
  return getModalRecipes().map((recipe) => ({
    id: recipe.id,
    label: recipe.label,
  }))
}
